
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import './styles/cyber-card.css';
import * as serviceWorker from './serviceWorker';
import { Capacitor } from '@capacitor/core';
import { isDesktopEnvironment } from './utils/desktop/desktopEnvironment';

// Global variable to indicate desktop environment
(window as any).isDesktopApp = isDesktopEnvironment();

// Check for basic browser features
const checkBrowserCapabilities = () => {
  // Create a list of all features we depend on that might be missing
  const requiredFeatures = [
    { name: 'Promise', available: typeof Promise !== 'undefined' },
    { name: 'fetch', available: typeof fetch !== 'undefined' },
    { name: 'localStorage', available: (() => { try { return !!window.localStorage; } catch (e) { return false; } })() },
    { name: 'sessionStorage', available: (() => { try { return !!window.sessionStorage; } catch (e) { return false; } })() },
  ];

  const missingFeatures = requiredFeatures.filter(feature => !feature.available);
  
  if (missingFeatures.length > 0) {
    console.warn('Browser missing required features:', missingFeatures.map(f => f.name).join(', '));
    return false;
  }
  
  // Additional capabilities check for optimal experience
  const optimalFeatures = [
    { name: 'ServiceWorker', available: 'serviceWorker' in navigator },
    { name: 'IndexedDB', available: 'indexedDB' in window },
  ];
  
  const missingOptimalFeatures = optimalFeatures.filter(feature => !feature.available);
  
  if (missingOptimalFeatures.length > 0) {
    console.warn('Browser missing optimal features. Some functionality may be limited:', 
      missingOptimalFeatures.map(f => f.name).join(', '));
  }
  
  return true;
};

// Initialize storage fallbacks for browsers with limitations
const initializeStorageFallbacks = () => {
  // Check if localStorage is available
  let storageAvailable = false;
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    storageAvailable = true;
  } catch (e) {
    console.warn('localStorage not available. Using memory storage fallback.');
    
    // Create in-memory storage fallback
    const memoryStorage: Record<string, string> = {};
    
    // Override localStorage methods
    Object.defineProperty(window, 'localStorage', {
      value: {
        setItem: (key: string, value: string) => { memoryStorage[key] = value; },
        getItem: (key: string) => memoryStorage[key] || null,
        removeItem: (key: string) => { delete memoryStorage[key]; },
        clear: () => { Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]); },
        key: (index: number) => Object.keys(memoryStorage)[index] || null,
        length: 0
      },
      writable: false
    });
    
    // Update length property with a getter
    Object.defineProperty(window.localStorage, 'length', {
      get: () => Object.keys(memoryStorage).length
    });
  }
  
  // Set a flag to indicate if we're in a desktop environment
  try {
    localStorage.setItem('isDesktopEnvironment', String(isDesktopEnvironment()));
  } catch (e) {
    console.warn('Could not save desktop environment flag:', e);
  }
  
  return storageAvailable;
};

// Enhanced service worker registration with error handling
const registerServiceWorkerSafely = () => {
  // Only register service worker in production and if supported
  if (!import.meta.env.PROD) {
    console.log('Service Worker disabled in development mode');
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported in this browser');
    return;
  }

  try {
    const serviceWorkerConfig = {
      onSuccess: (registration: ServiceWorkerRegistration) => {
        console.log('Service Worker registered successfully:', registration);
      },
      onUpdate: (registration: ServiceWorkerRegistration) => {
        console.log('New content is available, please refresh.');
      },
      onOffline: () => {
        console.log('App is offline. Limited functionality available.');
      },
      onOnline: () => {
        console.log('App is online. Full functionality available.');
      }
    };

    // Register with enhanced error handling
    serviceWorker.register(serviceWorkerConfig);
    
  } catch (error) {
    console.warn('Service Worker registration failed:', error);
    // Don't throw error - just log it and continue
  }
};

// Run initialization functions
const initializeApp = async () => {
  console.log('Initializing NALC Tracker application...');
  
  // Log environment information
  console.log('Environment:', {
    isDesktop: isDesktopEnvironment(),
    isNative: Capacitor.isNativePlatform(),
    userAgent: navigator.userAgent,
  });
  
  // Check browser features
  const hasRequiredFeatures = checkBrowserCapabilities();
  
  // Setup storage fallbacks if needed
  const hasStorage = initializeStorageFallbacks();
  
  // If Capacitor is available but desktop environment detected,
  // set a special flag to help with environment-specific behavior
  if (Capacitor.isNativePlatform() && isDesktopEnvironment()) {
    (window as any).isDesktopNative = true;
    console.log('Desktop native environment detected');
    
    try {
      localStorage.setItem('isDesktopNative', 'true');
    } catch (e) {
      console.warn('Could not save desktop native flag:', e);
    }
  }
  
  // Create root and render app
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Root element not found');
    return;
  }
  
  let root;
  try {
    // Try to use createRoot (React 18+)
    root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } catch (e) {
    // Fallback for older React versions or browsers
    console.warn('Modern ReactDOM.createRoot not available, using legacy render method');
    try {
      // @ts-ignore - fallback to legacy render for very old browsers
      ReactDOM.render(
        <React.StrictMode>
          <App />
        </React.StrictMode>,
        rootElement
      );
    } catch (err) {
      console.error('Failed to render application:', err);
      rootElement.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>Application Error</h2><p>There was a problem loading the application. Please try again or update your browser.</p></div>';
    }
  }
};

// Start the application
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error);
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>Application Error</h2><p>There was a problem loading the application. Please try refreshing the page or update your browser.</p></div>';
  }
});

// Register service worker safely after app initialization
setTimeout(() => {
  registerServiceWorkerSafely();
}, 1000);

// Export App as default for potential imports
export { App as default };
